import * as React from 'react';

import { cn } from '../../lib/utils';

/**
 * SegmentedControl — a row of mutually exclusive options styled as tabs.
 *
 * Deliberately matches TabsList/TabsTrigger so a filter reads as the same kind
 * of control as the page tabs above it. Use where a Select would otherwise hide
 * two or three short options behind a click.
 *
 * options: [{ value, label }]
 */
const SegmentedControl = React.forwardRef(({ options, value, onChange, className, size = 'default', ...props }, ref) => (
  <div
    ref={ref}
    role="tablist"
    className={cn(
      'inline-flex items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground',
      size === 'sm' ? 'h-8' : 'h-9',
      className
    )}
    {...props}
  >
    {options.map((option) => {
      const active = value === option.value;
      return (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onChange?.(option.value)}
          className={cn(
            'inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 font-medium transition-all',
            'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            size === 'sm' ? 'text-xs' : 'text-sm',
            active ? 'bg-background text-foreground shadow' : 'hover:text-foreground'
          )}
        >
          {option.label}
        </button>
      );
    })}
  </div>
));

SegmentedControl.displayName = 'SegmentedControl';

export { SegmentedControl };
